'use server';

import { apiRequest } from '@/utils/api-request';

export interface AcademicYear {
  id: number;
  year_label: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
}

export interface AcademicYearTransition {
  id: number;
  school_id: number;
  from_year_id: number | null;
  to_year_id: number;
  status: 'draft' | 'classes_generated' | 'promotions_mapped' | 'completed';
  classes_generated: number;
  students_promoted: number;
  students_graduated: number;
  students_held: number;
  initiated_by: number;
  completed_at: string | null;
  fromYear?: AcademicYear | null;
  toYear?: AcademicYear;
}

export interface PromotionMapping {
  from_class_id: number;
  from_class_name: string;
  student_count: number;
  suggested_to_class_id: number | null;
  suggested_to_class_name: string;
  action: 'promote' | 'graduate';
}

export interface OperationalClass {
  id: number;
  class_name: string;
}

// All routes go through dashboard service (port 5004) at /api/parent-app/admin/*
const ADMIN = '/api/parent-app/admin';

export async function getAcademicYears(): Promise<{ years: AcademicYear[]; activeYearId: number | null }> {
  try {
    const res = await apiRequest('/api/parent-app/academic-years', { method: 'GET' });
    const raw = res.data;
    // Handle both {years, activeYearId} object and legacy plain array
    if (Array.isArray(raw)) return { years: raw, activeYearId: null };
    if (raw && Array.isArray(raw.years)) return raw as { years: AcademicYear[]; activeYearId: number | null };
    return { years: [], activeYearId: null };
  } catch (e: any) { return { years: [], activeYearId: null }; }
}

export async function createAcademicYear(payload: { year_label: string; start_date: string; end_date: string }) {
  try {
    return await apiRequest(`${ADMIN}/academic-years`, { method: 'POST', body: JSON.stringify(payload) });
  } catch (e: any) { throw new Error(e.message || 'Failed to create academic year'); }
}

export async function activateAcademicYear(yearId: number) {
  try {
    return await apiRequest(`/api/parent-app/activate-academic-year/${yearId}`, { method: 'POST' });
  } catch (e: any) { throw new Error(e.message || 'Failed to activate year'); }
}

export async function getTransition(yearId: number): Promise<AcademicYearTransition> {
  try {
    const res = await apiRequest(`${ADMIN}/academic-years/${yearId}/transition`, { method: 'GET' });
    return res.data as AcademicYearTransition;
  } catch (e: any) { throw new Error(e.message || 'Failed to get transition'); }
}

export async function generateClasses(yearId: number) {
  try {
    return await apiRequest(`${ADMIN}/academic-years/${yearId}/transition/generate-classes`, { method: 'POST' });
  } catch (e: any) { throw new Error(e.message || 'Failed to generate classes'); }
}

export async function getPromotionPreview(yearId: number): Promise<{
  is_first_year: boolean;
  note?: string;
  mappings: PromotionMapping[];
  to_classes: OperationalClass[];
}> {
  try {
    const res = await apiRequest(`${ADMIN}/academic-years/${yearId}/transition/promotion-preview`, { method: 'GET' });
    return res.data as any;
  } catch (e: any) { throw new Error(e.message || 'Failed to fetch promotion preview'); }
}

export async function promoteStudents(yearId: number, mappings: { from_class_id: number; to_class_id: number | null }[]) {
  try {
    return await apiRequest(`${ADMIN}/academic-years/${yearId}/transition/promote-students`, {
      method: 'POST',
      body: JSON.stringify({ mappings })
    });
  } catch (e: any) { throw new Error(e.message || 'Failed to promote students'); }
}

export async function activateTransition(yearId: number) {
  try {
    return await apiRequest(`${ADMIN}/academic-years/${yearId}/transition/activate`, { method: 'POST' });
  } catch (e: any) { throw new Error(e.message || 'Failed to activate year'); }
}
